import {
  Archivierungsstatus,
  getArchivierungsstatusBeschreibung,
  getArchivierungsstatusLabel,
  isVorgangLockExpired,
} from '@core/domain';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Edit, Lock } from 'lucide-react';
import React from 'react';
import { useVorgangKommentare } from '@/adapter/opm-cockpit-backend';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { EnumFilter } from '@/components/filters/EnumFilter';
import { StatusMultiSelectFilter } from '@/components/filters/StatusMultiSelectFilter';
import { TextFilter } from '@/components/filters/TextFilter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { TableHeader } from '@/components/ui/TableHeader';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { STATUS_OPTIONS } from '@/constants/status';
import { VORGANG_PROZESS_SCHRITT_LABEL } from '@/constants/vorgangProzessSchritt';
import { User } from '@/features/auth/AuthProvider';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import { getStatusMapping, StatusVariant } from '@/lib/utils';
import { VorgangKommentarTable } from '@/pages/Vorgaenge/kommentare/VorgangKommentar';
import { VorgangListeItem } from '@/types';
import {
  type ColumnMatchMode,
  type DateRange,
  VorgangFilters,
} from '@/types/filters';
import { getColumnLabel } from './columnMeta';

interface StatusTooltipProps {
  vorgangId: string;
  statusText: string;
  badgeVariant: StatusVariant;
}

const StatusTooltip = ({
  vorgangId,
  statusText,
  badgeVariant,
}: StatusTooltipProps) => {
  const [open, setOpen] = React.useState(false);
  const {
    data: kommentare = [],
    isFetching,
    isLoading,
  } = useVorgangKommentare(vorgangId, {
    enabled: open,
  });
  const loading = isLoading || isFetching;

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <Badge variant={badgeVariant}>{statusText}</Badge>
      </TooltipTrigger>
      <TooltipContent className="p-0">
        <div className="max-h-[300px] min-w-[22rem] overflow-auto rounded-md border border-border bg-background text-[0.65rem] text-text-secondary">
          {loading ? (
            <div className="px-3 py-2 text-center text-sm text-text-primary">
              Kommentare werden geladen…
            </div>
          ) : !kommentare.length ? (
            <div className="px-3 py-2 text-center text-sm text-text-primary">
              Keine Kommentare gefunden.
            </div>
          ) : (
            <VorgangKommentarTable kommentare={kommentare} />
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const columnHelper = createColumnHelper<VorgangListeItem>();

export const getColumns = ({
  filters,
  updateFilters,
  isAllPageSelected,
  isSomePageSelected,
  toggleSelectAll,
  toggleSelection,
  selectedIds,
  onEdit,
  selectable = true,
  user,
  statusOptions = STATUS_OPTIONS,
  showCurrentStep = false,
  archiveFilters = false,
  createdAtFilterValue,
}: {
  filters: VorgangFilters;
  updateFilters: (newFilters: Partial<VorgangFilters>) => void;
  isAllPageSelected: boolean;
  isSomePageSelected: boolean;
  toggleSelectAll: () => void;
  toggleSelection: (id: string) => void;
  selectedIds: Set<string>;
  onEdit?: (item: VorgangListeItem) => void;
  selectable?: boolean;
  user: User;
  statusOptions?: { value: string; label: string }[];
  showCurrentStep?: boolean;
  archiveFilters?: boolean;
  createdAtFilterValue?: DateRange;
}) => {
  const getTextFilterProps = (
    field: 'auftragsnummer' | 'vertragsnummer' | 'externerName' | 'dokumentTyp',
  ) => {
    const operatorField = `${field}Operator` as const;
    return {
      value: filters[field],
      mode: archiveFilters ? (filters[operatorField] ?? 'contains') : undefined,
      onChange: (value: string, mode?: ColumnMatchMode) =>
        updateFilters({
          [field]: value,
          ...(archiveFilters ? { [operatorField]: mode } : {}),
        }),
    };
  };

  const selectColumn = columnHelper.display({
    id: 'select',
    header: () => (
      <Checkbox
        checked={isAllPageSelected}
        onCheckedChange={toggleSelectAll}
        aria-label="Select all"
        className={
          isSomePageSelected && !isAllPageSelected
            ? 'data-[state=checked]:bg-primary/50'
            : ''
        }
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds.has(row.original.id)}
        onCheckedChange={() => toggleSelection(row.original.id)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  });

  const actionsColumn = columnHelper.display({
    id: 'actions',
    header: getColumnLabel('actions'),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="cursor-pointer"
            onClick={() => onEdit?.(row.original)}
            disabled={
              !!row.original.lockedBy &&
              !isVorgangLockExpired(row.original.lockedAt) &&
              user?.email !== row.original.lockedBy
            }
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      );
    },
    enableSorting: false,
  });

  return [
    ...(selectable ? [selectColumn] : []),
    actionsColumn,
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <TableHeader title={getColumnLabel('status')}>
          <StatusMultiSelectFilter
            value={filters.status}
            onChange={(value) => updateFilters({ status: value })}
            options={statusOptions}
            placeholder="Status filtern..."
          />
        </TableHeader>
      ),
      cell: ({ getValue, row }) => {
        const status = getValue();
        const { text, variant } = getStatusMapping(
          status,
          row.original.deletedAt != null,
        );

        if (status === 'ZURUECKGESTELLT') {
          return (
            <StatusTooltip
              vorgangId={row.original.id}
              statusText={text}
              badgeVariant={variant}
            />
          );
        }

        return <Badge variant={variant}>{text}</Badge>;
      },
      enableSorting: false,
    }),

    ...(showCurrentStep
      ? [
          columnHelper.accessor('currentStep', {
            id: 'currentStep',
            header: () => (
              <TableHeader title={getColumnLabel('currentStep')}>
                <EnumFilter
                  value={filters.currentStep}
                  onChange={(value) =>
                    updateFilters({
                      currentStep: value as VorgangProzessSchritt | '',
                    })
                  }
                  options={Object.values(VorgangProzessSchritt).map((step) => ({
                    value: step,
                    label: VORGANG_PROZESS_SCHRITT_LABEL[step],
                  }))}
                  placeholder="Prozessschritt filtern..."
                />
              </TableHeader>
            ),
            cell: ({ getValue }) => {
              const step = getValue();
              if (!step) {
                return <span className="text-text-muted">—</span>;
              }
              return <span>{VORGANG_PROZESS_SCHRITT_LABEL[step] ?? step}</span>;
            },
            enableSorting: true,
          }),
        ]
      : []),

    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <TableHeader title={getColumnLabel('createdAt')}>
          <DateRangeFilter
            value={createdAtFilterValue ?? filters.createdAt}
            onChange={(value) => updateFilters({ createdAt: value })}
            placeholder="Datum filtern..."
          />
        </TableHeader>
      ),
      cell: (info) => {
        const dateString = info.getValue();
        try {
          const formattedDate = format(
            new Date(dateString),
            'dd.MM.yyyy HH:mm',
          );
          return <span className="text-text-secondary">{formattedDate}</span>;
        } catch {
          // Fallback to original value if date parsing fails
          return (
            <span className="text-text-secondary">
              {dateString.toISOString()}
            </span>
          );
        }
      },
    }),

    columnHelper.accessor('auftragsnummer', {
      id: 'auftragsnummer',
      header: () => (
        <TableHeader title={getColumnLabel('auftragsnummer')}>
          <TextFilter
            {...getTextFilterProps('auftragsnummer')}
            placeholder="Auftrag filtern..."
          />
        </TableHeader>
      ),
      enableSorting: false,
    }),

    columnHelper.accessor('vertragsnummer', {
      id: 'vertragsnummer',
      header: () => (
        <TableHeader title={getColumnLabel('vertragsnummer')}>
          <TextFilter
            {...getTextFilterProps('vertragsnummer')}
            placeholder="Vertrag filtern..."
          />
        </TableHeader>
      ),
      enableSorting: false,
    }),

    columnHelper.accessor('externeId', {
      id: 'externeId',
      header: () => (
        <TableHeader title={getColumnLabel('externeId')}>
          <TextFilter
            value={filters.externeId}
            onChange={(value) => updateFilters({ externeId: value })}
            placeholder="Event filtern..."
          />
        </TableHeader>
      ),
      enableSorting: false,
    }),

    columnHelper.accessor('externerName', {
      id: 'externerName',
      header: () => (
        <TableHeader
          title={getColumnLabel('externerName')}
          className="whitespace-nowrap min-w-[150px]"
        >
          <TextFilter
            {...getTextFilterProps('externerName')}
            placeholder="Event Name filtern..."
          />
        </TableHeader>
      ),
      enableSorting: false,
      cell: (info) => {
        const externerName = info.getValue();
        if (!externerName) {
          return <span className="text-text-secondary">-</span>;
        }
        return (
          <span className="text-text-primary font-mono text-sm">
            {externerName}
          </span>
        );
      },
    }),

    columnHelper.accessor('error', {
      id: 'error',
      header: () => (
        <TableHeader title={getColumnLabel('error')}>
          <TextFilter
            value={filters.error}
            onChange={(value) => updateFilters({ error: value })}
            placeholder="Fehler filtern..."
          />
        </TableHeader>
      ),
    }),

    columnHelper.accessor('fehlertyp', {
      id: 'fehlertyp',
      header: () => (
        <TableHeader title={getColumnLabel('fehlertyp')}>
          <EnumFilter
            value={filters.fehlertyp}
            onChange={(value) => updateFilters({ fehlertyp: value })}
            options={[
              { value: 'Fachlich', label: 'Fachlich' },
              { value: 'Technisch', label: 'Technisch' },
            ]}
            placeholder="Fehlertyp filtern..."
          />
        </TableHeader>
      ),
      enableSorting: false,
      cell: (info) => {
        const fehlertyp = info.getValue();
        if (!fehlertyp) {
          return <span className="text-text-secondary">-</span>;
        }
        return <span className="text-text-primary">{fehlertyp}</span>;
      },
    }),

    columnHelper.accessor('dokumentTyp', {
      id: 'dokumentTyp',
      header: () => (
        <TableHeader title={getColumnLabel('dokumentTyp')}>
          <TextFilter
            {...getTextFilterProps('dokumentTyp')}
            placeholder="Dokument filtern..."
          />
        </TableHeader>
      ),
      cell: (info) => {
        const dokumentTyp = info.getValue();
        if (!dokumentTyp) {
          return <span className="text-text-secondary">-</span>;
        }
        return <span className="text-text-primary">{dokumentTyp}</span>;
      },
    }),

    columnHelper.accessor('archivierungsstatus', {
      id: 'archivierungsstatus',
      header: () => (
        <TableHeader title={getColumnLabel('archivierungsstatus')}>
          <EnumFilter
            value={filters.archivierungsstatus ?? ''}
            onChange={(value) =>
              updateFilters({
                archivierungsstatus:
                  value as VorgangFilters['archivierungsstatus'],
              })
            }
            options={[
              { value: 'ERFOLGREICH', label: 'Ja' },
              { value: 'UEBERSPRUNGEN', label: 'Nein' },
            ]}
            placeholder="Archivierung filtern..."
          />
        </TableHeader>
      ),
      enableSorting: true,
      cell: (info) => {
        const status = info.getValue() ?? Archivierungsstatus.UNBEKANNT;
        const context = {
          status: info.row.original.status,
          currentStep: info.row.original.currentStep ?? null,
        };
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-text-primary">
                {getArchivierungsstatusLabel(status, context)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {getArchivierungsstatusBeschreibung(status, context)}
            </TooltipContent>
          </Tooltip>
        );
      },
    }),

    columnHelper.accessor('unterdrueckung', {
      id: 'unterdrueckung',
      header: () => (
        <TableHeader title={getColumnLabel('unterdrueckung')}>
          <TextFilter
            value={filters.unterdrueckung}
            onChange={(value) => updateFilters({ unterdrueckung: value })}
            placeholder="Unterdrückung filtern..."
          />
        </TableHeader>
      ),
      enableSorting: false,
      cell: (info) => {
        const unterdrueckung = info.getValue();
        if (!unterdrueckung) {
          return <span className="text-text-secondary">-</span>;
        }
        return <span className="text-text-primary">{unterdrueckung}</span>;
      },
    }),

    columnHelper.accessor('lockedBy', {
      id: 'lockedBy',
      header: () => (
        <TableHeader title={getColumnLabel('lockedBy')}>
          <TextFilter
            value={filters.lockedBy}
            onChange={(value) => updateFilters({ lockedBy: value })}
            placeholder="Gesperrt filtern..."
          />
        </TableHeader>
      ),
      enableSorting: false,
      cell: (info) => {
        const lockedBy = info.getValue();
        if (!lockedBy || isVorgangLockExpired(info.row.original.lockedAt))
          return null;

        return (
          <div className="flex items-center gap-1 text-yellow-600">
            <Lock className="h-3 w-3" />
            <span className="text-xs">{lockedBy}</span>
          </div>
        );
      },
    }),
  ];
};
